// What the driver drove at this employer.
//
// Multi-select, unlike the job title: a driver may have pulled a reefer on
// Monday and a flatbed on Thursday, and a recruiter screening for tanker
// experience needs every one of them rather than the most recent.
//
// Sixteen types is too many to read as chips on a phone, so the common ones
// show and the rest are one tap away.

import { useState } from 'react'

import {
  EmploymentEquipmentType,
  EmploymentEquipmentTypeLabels,
} from 'src/graphql/types/employmentExperiences'

import { SectionHeader } from './SectionHeader'

/** The ones most drivers pick, kept to two rows at phone width. */
const COMMON: EmploymentEquipmentType[] = [
  EmploymentEquipmentType.TRACTOR_TRAILER,
  EmploymentEquipmentType.DRY_VAN,
  EmploymentEquipmentType.REEFER,
  EmploymentEquipmentType.FLATBED,
  EmploymentEquipmentType.TANKER,
]

const REST = Object.values(EmploymentEquipmentType).filter(
  (type) => !COMMON.includes(type)
)

export interface EquipmentSectionProps {
  value: EmploymentEquipmentType[]
  onChange: (value: EmploymentEquipmentType[]) => void
}

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

const chip = (selected: boolean) =>
  join(
    'rounded-[7px] border px-2.5 py-1.5 text-[12px]',
    selected
      ? 'border-doubleNickel-brand-500 bg-doubleNickel-brand-25 font-semibold text-doubleNickel-brand-700'
      : 'border-doubleNickel-gray-300 bg-white font-medium text-doubleNickel-gray-700'
  )

export const EquipmentSection = ({
  value,
  onChange,
}: EquipmentSectionProps) => {
  // Opened when something outside the common set is already chosen, so a
  // selection is never hidden behind "Show all"
  const [showAll, setShowAll] = useState(
    value.some((type) => REST.includes(type))
  )
  const shown = showAll ? [...COMMON, ...REST] : COMMON

  const toggle = (type: EmploymentEquipmentType) =>
    onChange(
      value.includes(type)
        ? value.filter((chosen) => chosen !== type)
        : [...value, type]
    )

  return (
    <div className="rounded-[13px] border border-doubleNickel-gray-200 bg-white px-3 py-2.5">
      <SectionHeader
        title="Equipment operated"
        count={value.length ? `${value.length} selected` : undefined}
        complete={value.length > 0}
      />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {shown.map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={value.includes(type)}
            onClick={() => toggle(type)}
            className={chip(value.includes(type))}
          >
            {EmploymentEquipmentTypeLabels[type]}
          </button>
        ))}
        {!showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="rounded-[7px] px-2.5 py-1.5 text-[12px] font-semibold text-doubleNickel-brand-600"
          >
            Show all
          </button>
        )}
      </div>
    </div>
  )
}
